import {
  createSelector,
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AgentSchedule, Logger } from '@nice-ccf/core-sdk';
import { AgentScheduleForDisplay } from '@nice-ccf/ui-controls';
import { lighten, Theme } from '@mui/material';
import dayjs from 'dayjs';
import { CXoneClient } from '@nice-ccf/agent-sdk';

export const CCF_APP_SCHEDULER_KEY = 'appSchedule';
export const FETCHEVENTS = 'appSchedule/fetchWemScheduleEvents';
let logger: Logger;

interface CcfAppSchedule {
  events: AgentScheduleForDisplay[];
}

const initialCcfScheduleState: CcfAppSchedule = {
  events: [],
};

/**
 * get Agent shift and Events
 * @example - dispatch(getAgentEvents())
 */
export const fetchWemScheduleEvents = createAsyncThunk(
  FETCHEVENTS,
  async (
    {
      currentDate = new Date(),
      currentView = 'day',
      theme,
    }: { currentDate?: Date; currentView?: string; theme: Theme },
    { dispatch }
  ) => {
    try {
      const view = currentView;
      let startDate = currentDate;
      let endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 1);
      if (view === 'week') {
        startDate = new Date(
          currentDate.setDate(currentDate.getDate() - currentDate.getDay())
        );
        endDate = new Date(
          currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 6)
        );
      }
      startDate.setHours(0, 0, 0);
      endDate.setHours(23, 59, 59);
      dispatch(scheduleActions.storeEvents([]));
      const resp = await CXoneClient.instance.wemSchedule.getSchedule(
        startDate,
        endDate
      );
      const events = resp as AgentSchedule[];
      const shiftsAsBackground = events
        .filter((event) => event.title === 'shift')
        .map(
          (event: AgentSchedule) =>
            ({
              end: event.end,
              start: event.start,
              className: 'shift-background',
              display: 'background',
              extendedProps: {
                description: event.notes,
                borderHighlightColor: event.backgroundColor,
              },
              backgroundColor: event.backgroundColor,
              allDay: false,
              notes: event.notes,
              id: event.id,
              title: '',
            } as AgentScheduleForDisplay)
        );
      const eventsWithBackgroundShift = events.concat(shiftsAsBackground);
      const updatedResponse = eventsWithBackgroundShift.map(
        (event: AgentSchedule) =>
          ({
            ...event,
            className: event.title === 'shift' ? 'shift' : event.title,
            end:
              event.title === 'shift' &&
              !(event.end.getHours() === 0 && event.end.getMinutes() === 0)
                ? dayjs(event.end).add(1, 'day').format()
                : event.end,
            extendedProps: {
              description: event.notes,
              borderHighlightColor: event.backgroundColor,
            },
            backgroundColor:
              event.title === 'shift'
                ? lighten('#dae2e8', 0.3)
                : lighten(event.backgroundColor, 0.9),
            allDay: event.title === 'shift' ? true : false,
            title:
              event.title === 'shift'
                ? `${dayjs(event.start).format('hh:mm A')} - ${dayjs(
                    event.end
                  ).format('hh:mm A')}`
                : event.title,
            isWEMEvent: true,
          } as AgentScheduleForDisplay)
      );
      return updatedResponse;
    } catch (error) {
      logger.error(
        '[scheduleSlice][getAgentEvents]',
        `payload: ${JSON.stringify(error)}`
      );
      return [];
    }
  }
);

export const scheduleSlice = createSlice({
  name: CCF_APP_SCHEDULER_KEY,
  initialState: initialCcfScheduleState,
  reducers: {
    /**
     * Reducer function to store events
     * @param state - AppState
     * @param action - action.payload
     * @example - dispatch(storeEvents([]));
     * @returns - updated state
     */
    storeEvents(state, action: PayloadAction<AgentScheduleForDisplay[]>) {
      return {
        ...state,
        events: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWemScheduleEvents.fulfilled, (state, action) => {
      return {
        ...state,
        events: [...(action.payload as AgentScheduleForDisplay[])],
      };
    });
  },
});

export const CcfAppScheduleReducer = scheduleSlice.reducer;

export const scheduleActions = scheduleSlice.actions;
/**
 * @param rootState - root state
 * @example - getAppScheduleState
 */
const getAppScheduleState = (rootState: { appSchedule: CcfAppSchedule }) => {
  return rootState[CCF_APP_SCHEDULER_KEY];
};

export const getWemScheduleEvents = createSelector(
  getAppScheduleState,
  (appSchedule: CcfAppSchedule) => appSchedule.events
);
